/**
 * SerialNumberModal — manage component serial numbers for a BOM line.
 * Phase 25b: Unit serial number dropdown to tag components to specific units.
 */

import { useCallback, useEffect, useState } from 'react';
import {
  fetchSerialNumbers, createSerialNumber, updateSerialNumber, deleteSerialNumber,
  fetchUnitSerialNumbers,
} from '../../../api/files';
import { Button, LoadingSpinner } from '../../common';

export default function SerialNumberModal({ line, onClose, onChanged }) {
  const [serials, setSerials] = useState([]);
  const [unitSerials, setUnitSerials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newSN, setNewSN] = useState('');
  const [newUnitSN, setNewUnitSN] = useState(''); // UUID of selected unit serial number
  const [newNotes, setNewNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editValues, setEditValues] = useState({});

  const jobId = line.job;

  const loadData = useCallback(async () => {
    try {
      const [snData, usnData] = await Promise.all([
        fetchSerialNumbers({ job_bom_line: line.id }),
        fetchUnitSerialNumbers({ job: jobId }),
      ]);
      setSerials(snData);
      setUnitSerials(usnData);
      // Auto-select first unit if only one
      if (usnData.length === 1 && !newUnitSN) {
        setNewUnitSN(usnData[0].id);
      }
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  }, [line.id, jobId]);

  useEffect(() => { loadData(); }, [loadData]);

  async function handleAdd() {
    if (!newSN.trim()) return;
    setSaving(true);
    try {
      const payload = {
        job_bom_line: line.id,
        serial_number: newSN.trim(),
        unit_number: getUnitIndex(newUnitSN),
        notes: newNotes.trim(),
      };
      if (newUnitSN) payload.unit_serial_number = newUnitSN;
      await createSerialNumber(payload);
      setNewSN('');
      setNewNotes('');
      loadData();
      onChanged?.();
    } catch (err) {
      alert(err.response?.data?.serial_number?.[0] || err.response?.data?.error || 'Failed to add');
    } finally {
      setSaving(false);
    }
  }

  function getUnitIndex(usnId) {
    if (!usnId) return 1;
    const usn = unitSerials.find((u) => u.id === usnId);
    return usn?.unit_index || 1;
  }

  function getUnitLabel(sn) {
    if (sn.unit_serial_display) return sn.unit_serial_display;
    // Fallback: find from loaded unit serials
    if (sn.unit_serial_number) {
      const usn = unitSerials.find((u) => u.id === sn.unit_serial_number);
      return usn?.serial_number || '';
    }
    return '';
  }

  async function handleSaveEdit(id) {
    try {
      await updateSerialNumber(id, editValues);
      setEditingId(null);
      setEditValues({});
      loadData();
      onChanged?.();
    } catch (err) {
      alert(err.response?.data?.error || 'Save failed');
    }
  }

  async function handleDelete(id) {
    if (!confirm('Delete this serial number?')) return;
    try {
      await deleteSerialNumber(id);
      loadData();
      onChanged?.();
    } catch {
      alert('Delete failed');
    }
  }

  function startEdit(sn) {
    setEditingId(sn.id);
    setEditValues({
      serial_number: sn.serial_number,
      unit_serial_number: sn.unit_serial_number || '',
      unit_number: sn.unit_number,
      notes: sn.notes || '',
    });
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={onClose}>
      <div
        className="bg-white rounded-lg shadow-xl w-full max-w-lg mx-4 max-h-[80vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-5 py-4 border-b border-gray-200">
          <h3 className="text-base font-semibold text-gray-900">Serial Numbers</h3>
          <p className="text-sm text-gray-500 mt-0.5">
            {line.part_display} — {line.part_description || ''}
          </p>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-4">
          {loading ? (
            <div className="flex justify-center py-6"><LoadingSpinner /></div>
          ) : (
            <>
              {/* Existing serial numbers */}
              {serials.length > 0 ? (
                <div className="space-y-2 mb-4">
                  {serials.map((sn) => (
                    <div key={sn.id} className="flex items-center gap-2 bg-gray-50 rounded-lg px-3 py-2">
                      {editingId === sn.id ? (
                        <>
                          <input
                            type="text"
                            className="flex-1 border border-blue-300 rounded px-2 py-1 text-sm"
                            value={editValues.serial_number}
                            onChange={(e) => setEditValues({ ...editValues, serial_number: e.target.value })}
                            autoFocus
                          />
                          {unitSerials.length > 0 && (
                            <select
                              className="w-28 border border-blue-300 rounded px-1 py-1 text-sm"
                              value={editValues.unit_serial_number || ''}
                              onChange={(e) => setEditValues({
                                ...editValues,
                                unit_serial_number: e.target.value || null,
                                unit_number: getUnitIndex(e.target.value),
                              })}
                            >
                              <option value="">No unit</option>
                              {unitSerials.map((usn) => (
                                <option key={usn.id} value={usn.id}>{usn.serial_number}</option>
                              ))}
                            </select>
                          )}
                          <button onClick={() => handleSaveEdit(sn.id)} className="text-green-600 text-xs font-medium">Save</button>
                          <button onClick={() => setEditingId(null)} className="text-gray-400 text-xs">Cancel</button>
                        </>
                      ) : (
                        <>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-semibold text-gray-900">{sn.serial_number}</div>
                            <div className="text-xs text-gray-500 flex items-center gap-1.5">
                              {getUnitLabel(sn) ? (
                                <span className="px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 font-medium text-[10px]">
                                  Unit S/N: {getUnitLabel(sn)}
                                </span>
                              ) : (
                                <span>Unit {sn.unit_number}</span>
                              )}
                              {sn.notes && <span className="text-gray-400">— {sn.notes}</span>}
                            </div>
                          </div>
                          <button
                            onClick={() => startEdit(sn)}
                            className="text-blue-500 hover:text-blue-700 text-xs font-medium"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleDelete(sn.id)}
                            className="text-red-400 hover:text-red-600 text-xs font-medium"
                          >
                            Del
                          </button>
                        </>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-400 mb-4">No serial numbers entered yet.</p>
              )}

              {/* Add new */}
              <div className="border-t border-gray-200 pt-3">
                <div className="text-xs font-semibold text-gray-500 uppercase mb-2">Add Serial Number</div>
                <div className="flex items-end gap-2">
                  <div className="flex-1">
                    <label className="text-xs text-gray-500">Component S/N</label>
                    <input
                      type="text"
                      className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm focus:ring-1 focus:ring-blue-500"
                      placeholder="Enter serial number"
                      value={newSN}
                      onChange={(e) => setNewSN(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
                      autoFocus={serials.length === 0}
                    />
                  </div>
                  {unitSerials.length > 0 && (
                    <div className="w-28">
                      <label className="text-xs text-gray-500">Unit S/N</label>
                      <select
                        className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm"
                        value={newUnitSN}
                        onChange={(e) => setNewUnitSN(e.target.value)}
                      >
                        {unitSerials.length > 1 && <option value="">— Select —</option>}
                        {unitSerials.map((usn) => (
                          <option key={usn.id} value={usn.id}>
                            {usn.serial_number}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                  <Button size="sm" onClick={handleAdd} loading={saving} disabled={!newSN.trim()}>
                    Add
                  </Button>
                </div>
                <div className="mt-2">
                  <label className="text-xs text-gray-500">Notes (optional)</label>
                  <input
                    type="text"
                    className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm"
                    placeholder="e.g., location, condition"
                    value={newNotes}
                    onChange={(e) => setNewNotes(e.target.value)}
                  />
                </div>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-gray-200 flex justify-between items-center">
          <span className="text-xs text-gray-400">
            {serials.length} serial number{serials.length !== 1 ? 's' : ''}
          </span>
          <Button size="sm" variant="ghost" onClick={onClose}>Close</Button>
        </div>
      </div>
    </div>
  );
}
